import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ScanLine, FilePen, Undo2, AlarmClock, CalendarDays, CalendarRange, Gauge, Hourglass, BadgeCheck, BookCheck, AlertTriangle, ChevronRight, Landmark, TrendingDown, Server } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, PageHeader, Spinner } from "@/components/common";

const ALERT_TYPES = { contrat: "Contrat", garantie: "Garantie", budget: "Budget", retard: "Retard", compte: "Compte désactivé" };

function Alerts({ alerts }) {
  const nav = useNavigate();
  const [type, setType] = useState("");
  const list = alerts.filter((a) => !type || a.type === type);
  return (
    <div className="card rise" data-testid="alerts-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E4EC] p-4">
        <h2 className="mr-auto flex items-center gap-2 text-lg font-bold"><AlertTriangle size={18} className="text-[#A5894E]" /> Alertes <span className="text-sm text-[#6B6B80]">({alerts.length})</span></h2>
        {["", ...Object.keys(ALERT_TYPES).filter((t) => alerts.some((a) => a.type === t))].map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`alert-filter-${t || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${type === t ? "border-[#16163F] bg-[#16163F] text-white" : "border-[#E4E4EC] hover:border-[#A5894E]"}`}>
            {t ? ALERT_TYPES[t] : "Toutes"} ({t ? alerts.filter((a) => a.type === t).length : alerts.length})
          </button>
        ))}
      </div>
      <div className="max-h-[460px] divide-y divide-[#E4E4EC] overflow-auto">
        {list.map((a, i) => (
          <button key={i} onClick={() => nav(a.link)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#A5894E]/10" data-testid={`alert-item-${i}`}>
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${a.level === "danger" ? "bg-[#B03A3A]" : "bg-[#A5894E]"}`} />
            <span className="w-28 shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#6B6B80]">{ALERT_TYPES[a.type]}</span>
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{a.title}</span><span className="block truncate text-xs text-[#6B6B80]">{a.detail}</span></span>
            <ChevronRight size={16} className="text-[#6B6B80]" />
          </button>
        ))}
        {!list.length && <p className="p-8 text-center text-sm text-[#6B6B80]">Aucune alerte.</p>}
      </div>
    </div>
  );
}

function ChefBoard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/chef").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const c = d.cards;
  const cards = [
    [ScanLine, "Factures scannées à traiter", c.scans_to_process, "/factures-a-traiter", "kpi-scans-to-process"],
    [FilePen, "Factures en brouillon", c.drafts, "/factures?status=brouillon", "kpi-drafts"],
    [Undo2, "Retournées par la comptabilité", c.returned, "/factures?status=retournee", "kpi-returned"],
    [AlarmClock, "En retard de paiement", c.overdue, "/factures?status=en_retard", "kpi-overdue"],
    [CalendarDays, "Dépenses du mois (HT)", eur(c.spent_month), "/kpi", "kpi-spent-month"],
    [CalendarRange, "Dépenses de l'année (HT)", eur(c.spent_year), "/kpi", "kpi-spent-year"],
    [Gauge, "Budget consommé", `${String(c.budget_pct).replace(".", ",")} %`, "/budgets", "kpi-budget-pct"],
  ];
  return (
    <>
      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(([icon, label, value, link, id], i) => <KpiCard key={id} icon={icon} label={label} value={value} testid={id} onClick={() => nav(link)} delay={i * 50} />)}
      </div>
      <Alerts alerts={d.alerts} />
    </>
  );
}

function ComptaBoard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/compta").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const c = d.cards;
  return (
    <>
      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={Hourglass} label="En attente de contrôle" value={c.pending_control} testid="kpi-pending-control" onClick={() => nav("/factures?status=transmise")} />
        <KpiCard icon={BadgeCheck} label="Bon à payer" value={c.to_pay} hint={eur(c.to_pay_amount)} testid="kpi-to-pay" onClick={() => nav("/factures?status=bon_a_payer")} delay={50} />
        <KpiCard icon={BookCheck} label="Comptabilisé ce mois (TTC)" value={eur(c.accounted_month)} testid="kpi-accounted-month" onClick={() => nav("/comptabilite")} delay={100} />
        <KpiCard icon={AlarmClock} label="Factures en retard" value={c.overdue} testid="kpi-compta-overdue" onClick={() => nav("/factures?status=en_retard")} delay={150} />
        <KpiCard icon={Landmark} label="Immobilisations à confirmer" value={c.fa_to_confirm} testid="kpi-fa-to-confirm" onClick={() => nav("/immobilisations")} delay={200} />
        <KpiCard icon={TrendingDown} label="Dotations de l'exercice en cours" value={eur(c.fa_dotations_year)} testid="kpi-fa-dotations" onClick={() => nav("/immobilisations")} delay={250} />
        <KpiCard icon={Server} label="VNC totale du parc informatique" value={eur(c.fa_vnc_it)} testid="kpi-fa-vnc" onClick={() => nav("/immobilisations")} delay={300} />
      </div>
      <div className="card rise p-6" data-testid="forecast-chart">
        <h2 className="text-lg font-bold">Prévisionnel de décaissement – 8 semaines</h2>
        <p className="mb-4 text-xs text-[#6B6B80]">Factures transmises et bon à payer par semaine d'échéance (les échéances dépassées sont reportées sur la semaine en cours).</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={d.forecast}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4EC" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B6B80" }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)} k€`} tick={{ fontSize: 11, fill: "#6B6B80" }} />
            <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => `Semaine ${l}`} />
            <Bar dataKey="amount" name="Montant TTC" fill="#16163F" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const chef = user.role === "createur";
  return (
    <div>
      <PageHeader title={chef ? "Tableau de bord Créateur" : "Tableau de bord Comptabilité"} subtitle={`Bonjour ${user.name.split(" ")[0]}, ${chef ? `voici l'activité de vos services (${(user._ccs || user.cost_centers || []).join(", ") || "aucun rattachement"}).` : "voici l'activité de facturation des sociétés sélectionnées."}`} />
      {chef ? <ChefBoard /> : <ComptaBoard />}
    </div>
  );
}
